import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { storeActivityIndicator } from '../../../../../redux/reducers/authSlice';
import { HgsExcelSonucu, HgsOgsService, HgsSecenekleri, tutarYaz } from '../../../../../services/HgsOgsService';

interface Props {
    show: boolean
    onClose: () => void
    onAktarildi: () => void
    ornekExcel: () => void
}

/** Hücreler: metin kelimeden kırılır (yatay kaydırma olmasın); sayılar sağa dayalı, tek satır. */
const HUCRE: React.CSSProperties = { paddingLeft: '0.5rem', paddingRight: '0.5rem', whiteSpace: 'normal', wordBreak: 'break-word' };
const SAYI: React.CSSProperties = { paddingLeft: '0.5rem', paddingRight: '0.75rem', textAlign: 'right', whiteSpace: 'nowrap' };
/** Başlık: liste ekranlarındaki standart renk (#8DC1FF, siyah yazı); gerekirse iki satıra kırılır. */
const BASLIK: React.CSSProperties = { ...HUCRE, background: '#8DC1FF', color: '#000', verticalAlign: 'bottom', lineHeight: 1.3 };

/**
 * Excel'den içe aktarma (26.10.2026, #488): dosya(lar) seç → Önizle (hiçbir şey kaydedilmez) → hata yoksa Aktar. İki biçim
 * tanınır: Keylogx örnek Excel'i ve Ziraat Bankası HGS geçiş dökümü (bankadan indirildiği gibi; her dosya bir araç, birden
 * çok dosya birlikte seçilebilir). Hatalı satır varsa hiçbir dosyadan satır aktarılmaz; hatalar satır numarasıyla
 * gösterilir. Mükerrer satır ve daha önce aktarılmış dosya uyarıdır.
 */
const ExcelIceAktarModal = ({ show, onClose, onAktarildi, ornekExcel }: Props) => {
    const dispatch = useDispatch();
    const [dosyalar, setDosyalar] = useState<File[]>([]);
    const [sonuc, setSonuc] = useState<HgsExcelSonucu | undefined>(undefined);
    const [yalnizHatalilar, setYalnizHatalilar] = useState<boolean>(false);
    // Olduğu gibi yüklenebilen banka dökümleri sunucudan gelir; yeni banka eklenince ekran kendiliğinden günceller.
    const [bankalar, setBankalar] = useState<string[]>([]);
    const girdi = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!show || bankalar.length > 0)
            return;
        HgsOgsService.secenekler()
            .then(r => { if (r.isSuccess) setBankalar((r.data as HgsSecenekleri).taninanDokumler ?? []); })
            .catch(() => { });
    }, [show])

    useEffect(() => {
        setDosyalar([]);
        setSonuc(undefined);
        setYalnizHatalilar(false);
        if (girdi.current)
            girdi.current.value = '';
    }, [show])

    const onizle = async (secilen: File[] = dosyalar) => {
        if (secilen.length == 0) {
            toast.error('Önce Excel dosyasını seçin.');
            return;
        }

        try {
            dispatch(storeActivityIndicator(true));

            var response = await HgsOgsService.excelOnizle(secilen);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setSonuc(response.data);
            setYalnizHatalilar((response.data as HgsExcelSonucu).hataliSatir > 0);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const aktar = async () => {
        if (dosyalar.length == 0 || !sonuc)
            return;

        try {
            dispatch(storeActivityIndicator(true));

            var response = await HgsOgsService.excelAktar(dosyalar);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            const s = response.data as HgsExcelSonucu;
            if (s.aktarilan > 0) {
                toast.success(`${s.aktarilan} kayıt aktarıldı.`);
                onAktarildi();
            }
            else {
                setSonuc(s);
                toast.error(s.hata ?? 'Hatalı satırlar var; hiçbir kayıt aktarılmadı.');
            }
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const aktarilabilir = !!sonuc && !sonuc.hata && sonuc.hataliSatir == 0 && sonuc.satirSayisi > 0;
    const satirlar = (sonuc?.satirlar ?? []).filter(x => !yalnizHatalilar || x.hatalar.length > 0);
    const cokDosya = (sonuc?.dosyalar ?? []).length > 1;

    return (
        <Modal show={show} onHide={onClose} size="xl" centered scrollable dialogClassName="hgs-ice-aktar">
            <Modal.Header closeButton>
                <Modal.Title>Excel'den içe aktar · HGS</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex flex-wrap align-items-center gap-3 mb-4">
                    <input
                        id="hgs-excel-dosya"
                        ref={girdi}
                        type="file"
                        accept=".xlsx,.xls"
                        multiple
                        className="form-control form-control-sm w-auto"
                        onChange={e => { const f = Array.from(e.target.files ?? []); setDosyalar(f); setSonuc(undefined); if (f.length > 0) onizle(f); }}
                    />
                    <button className="btn btn-sm btn-light-primary" disabled={dosyalar.length == 0} onClick={() => onizle()}>Yeniden önizle</button>
                    <button className="btn btn-sm btn-light-success" onClick={ornekExcel}><i className="fa fa-file-excel me-1" />Örnek Excel indir</button>
                    {
                        bankalar.length > 0 && (
                            <div className="d-flex flex-wrap align-items-center gap-2 fs-7">
                                <span className="text-muted">Tanınan banka dökümleri:</span>
                                {bankalar.map(b => <span key={b} className="badge badge-light-primary fs-7"><i className="fa fa-building-columns me-1" />{b}</span>)}
                            </div>
                        )
                    }
                </div>

                <div className="text-muted fs-7 mb-4">
                    {bankalar.length > 0 && <React.Fragment><b>{bankalar.join(', ')}</b> HGS geçiş dökümünü bankadan indirdiğiniz gibi yükleyebilirsiniz; her araç için ayrı dosya varsa hepsini birlikte seçin. </React.Fragment>}
                    Listede olmayan bir bankanın ya da PTT'nin dökümü için örnek Excel'i doldurun: 2. satır örnektir, kayıtlarınızı altına girip örnek satırı silin (silmezseniz atlanır).
                    Önizlemede hiçbir şey kaydedilmez. Hatalı satır varsa hiçbir satır aktarılmaz; hataları düzeltip dosyayı yeniden seçin.
                </div>

                {sonuc?.hata && <div className="alert alert-danger py-3">{sonuc.hata}</div>}

                {
                    sonuc && !sonuc.hata && (
                        <React.Fragment>
                            {
                                (sonuc.dosyalar ?? []).length > 0 && (
                                    <div className="fs-7 mb-3">
                                        {sonuc.dosyalar.map(d => (
                                            <div key={d.ad}><i className="fa fa-file-excel text-success me-2" />{d.ad} · <span className="text-muted">{d.bicim}</span> · {d.satirSayisi} satır{d.atlananSifirSatir > 0 ? ` (${d.atlananSifirSatir} sıfır tutarlı satır atlandı)` : ''}</div>
                                        ))}
                                    </div>
                                )
                            }

                            <div className="d-flex flex-wrap gap-6 mb-3 fs-6">
                                <span><b>{sonuc.satirSayisi}</b> satır</span>
                                <span className={sonuc.hataliSatir > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>{sonuc.hataliSatir} hatalı</span>
                                {sonuc.mukerrerSatir > 0 && <span className="text-warning fw-bold">{sonuc.mukerrerSatir} mükerrer</span>}
                                <span>Maliyet toplamı: <b>{tutarYaz(sonuc.toplamTL)} TL</b></span>
                                {sonuc.ornekSatirAtlandi && <span className="text-muted">Örnek satır atlandı</span>}
                                {sonuc.atlananSifirSatir > 0 && <span className="text-muted" title="Tutarı 0 ve gişesi boş satırlar gerçek geçiş değildir (bankanın yükleme öncesi kaydı); kaydedilmez.">{sonuc.atlananSifirSatir} sıfır tutarlı satır atlandı</span>}
                            </div>

                            {sonuc.ayniDosyaUyarisi && <div className="alert alert-warning py-2">{sonuc.ayniDosyaUyarisi}</div>}
                            {sonuc.mukerrerSatir > 0 && <div className="alert alert-light-warning py-2">Mükerrer işaretli satırlar sistemde ya da seçilen dosyalarda zaten var (aynı araç, giriş zamanı, giriş yeri, ücret ve tür). Aktarırsanız ikinci kez kaydedilir.</div>}

                            {
                                sonuc.hataliSatir > 0 && (
                                    <label className="form-check form-check-sm form-check-custom form-check-solid mb-3">
                                        <input id="hgs-yalniz-hatalilar" className="form-check-input" type="checkbox" checked={yalnizHatalilar} onChange={() => setYalnizHatalilar(!yalnizHatalilar)} />
                                        <span className="form-check-label">Yalnız hatalı satırları göster</span>
                                    </label>
                                )
                            }

                            {/* Sabit yerleşim + yüzde genişlikler: yatay kaydırma olmasın, başlıklar değerlerle hizalı (otomatik fatura önizlemesi gibi). */}
                            <table className="table table-row-dashed ebbe-compact-table fs-7 align-middle otofatura-onizleme-tablo" style={{ tableLayout: 'fixed', width: '100%' }}>
                                <colgroup>
                                    {cokDosya && <col style={{ width: '8%' }} />}
                                    <col style={{ width: '4%' }} />
                                    <col style={{ width: '5%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: cokDosya ? '9%' : '11%' }} />
                                    <col style={{ width: '9%' }} />
                                    <col style={{ width: cokDosya ? '9%' : '11%' }} />
                                    <col style={{ width: cokDosya ? '9%' : '11%' }} />
                                    <col style={{ width: '6%' }} />
                                    <col style={{ width: '4%' }} />
                                    <col style={{ width: '6%' }} />
                                    <col style={{ width: cokDosya ? '5%' : '7%' }} />
                                    <col style={{ width: cokDosya ? '10%' : '10%' }} />
                                </colgroup>
                                <thead>
                                    <tr className="fw-bolder text-uppercase">
                                        {cokDosya && <th style={BASLIK}>Dosya</th>}
                                        <th style={BASLIK}>Satır</th>
                                        <th style={BASLIK}>Tür</th>
                                        <th style={BASLIK}>Plaka</th>
                                        <th style={BASLIK}>Giriş</th>
                                        <th style={BASLIK}>Giriş Yeri</th>
                                        <th style={BASLIK}>Çıkış</th>
                                        <th style={BASLIK}>Çıkış Yeri</th>
                                        <th style={BASLIK}>İşletme</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>Ücret</th>
                                        <th style={BASLIK}>Döviz</th>
                                        <th className="sayi" style={{ ...BASLIK, textAlign: 'right' }}>TL</th>
                                        <th style={BASLIK}>Sefer No</th>
                                        <th style={BASLIK}>Durum</th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-700 fw-semibold">
                                    {satirlar.length == 0 && <tr><td colSpan={14} className="text-center text-muted">Gösterilecek satır yok.</td></tr>}
                                    {
                                        satirlar.map(s => (
                                            <tr key={(s.dosya ?? '') + '-' + s.satirNo} className={s.hatalar.length > 0 ? 'bg-light-danger' : undefined}>
                                                {cokDosya && <td style={HUCRE}>{s.dosya}</td>}
                                                <td style={HUCRE}>{s.satirNo}</td>
                                                <td style={HUCRE}>{s.islemTuru}</td>
                                                <td style={HUCRE}>{s.plaka ?? (s.etiketNo ? 'Etiket ' + s.etiketNo : '')}</td>
                                                <td style={HUCRE}>{s.islemTarihi}{s.islemSaati ? ' ' + s.islemSaati : ''}</td>
                                                <td style={HUCRE}>{s.girisYeri}</td>
                                                <td style={HUCRE}>{s.cikis}</td>
                                                <td style={HUCRE}>{s.cikisYeri}</td>
                                                <td style={HUCRE}>{s.isletme}</td>
                                                <td className="sayi" style={SAYI}>{tutarYaz(s.tutar)}</td>
                                                <td style={HUCRE}>{s.dovizCinsi}</td>
                                                <td className="sayi" style={SAYI}>{tutarYaz(s.tutarTL)}</td>
                                                <td style={HUCRE}>{s.seferNo}</td>
                                                <td style={HUCRE}>
                                                    {s.hatalar.length > 0
                                                        ? <ul className="text-danger mb-0 ps-4">{s.hatalar.map((h, i) => <li key={i}>{h}</li>)}</ul>
                                                        : s.mukerrer ? <span className="badge badge-light-warning">Mükerrer</span> : <span className="badge badge-light-success">Uygun</span>}
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </React.Fragment>
                    )
                }
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-sm btn-light" onClick={onClose}>Kapat</button>
                <button className="btn btn-sm btn-primary" disabled={!aktarilabilir} onClick={aktar}>
                    {sonuc && sonuc.satirSayisi > 0 ? `${sonuc.satirSayisi - sonuc.hataliSatir} kaydı aktar` : 'Aktar'}
                </button>
            </Modal.Footer>
        </Modal>
    )
}

export default ExcelIceAktarModal;
